import { useCallback, useRef } from 'react';

export const useCellNavigation = ({ isMultiFunds, fundKeys }) => {
  const cellRefs = useRef(new Map());
  const cellOrder = useRef([]);

  const registerCell = useCallback((cellKey, ref) => {
    if (ref) {
      cellRefs.current.set(cellKey, ref);
      if (!cellOrder.current.includes(cellKey)) {
        cellOrder.current.push(cellKey);
      }
    } else {
      cellRefs.current.delete(cellKey);
      cellOrder.current = cellOrder.current.filter(key => key !== cellKey);
    }
  }, []);

  const navigateToCell = useCallback(
    (direction, currentCellKey) => {
      const currentIndex = cellOrder.current.indexOf(currentCellKey);
      if (currentIndex === -1 || cellOrder.current.length === 0) {
        return;
      }

      let nextIndex = currentIndex;
      const totalCells = cellOrder.current.length;

      const currentParts = currentCellKey.split('-');
      let currentColumn, currentFund, currentQuestion;

      if (isMultiFunds) {
        currentFund = currentParts[currentParts.length - 1];
        currentColumn = currentParts[currentParts.length - 2];
        currentQuestion = currentParts.slice(0, -2).join('-');
      } else {
        currentColumn = currentParts[currentParts.length - 1];
        currentQuestion = currentParts.slice(0, -1).join('-');
      }

      if (direction === 'ArrowRight') {
        if (isMultiFunds) {
          const currentFundIndex = fundKeys.indexOf(currentFund);
          if (currentFundIndex < fundKeys.length - 1) {
            const nextFund = fundKeys[currentFundIndex + 1];
            const nextKey = `${currentQuestion}-${currentColumn}-${nextFund}`;
            const foundIndex = cellOrder.current.indexOf(nextKey);
            if (foundIndex !== -1) {
              nextIndex = foundIndex;
            } else {
              nextIndex = (currentIndex + 1) % totalCells;
            }
          } else {
            nextIndex = (currentIndex + 1) % totalCells;
          }
        } else {
          nextIndex = (currentIndex + 1) % totalCells;
        }
      } else if (direction === 'ArrowLeft') {
        if (isMultiFunds) {
          const currentFundIndex = fundKeys.indexOf(currentFund);
          if (currentFundIndex > 0) {
            const prevFund = fundKeys[currentFundIndex - 1];
            const prevKey = `${currentQuestion}-${currentColumn}-${prevFund}`;
            const foundIndex = cellOrder.current.indexOf(prevKey);
            if (foundIndex !== -1) {
              nextIndex = foundIndex;
            } else {
              nextIndex = currentIndex - 1;
              if (nextIndex < 0) nextIndex = totalCells - 1;
            }
          } else {
            nextIndex = currentIndex - 1;
            if (nextIndex < 0) nextIndex = totalCells - 1;
          }
        } else {
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) nextIndex = totalCells - 1;
        }
      } else if (direction === 'ArrowDown') {
        let found = false;
        for (let i = currentIndex + 1; i < totalCells; i++) {
          const nextKey = cellOrder.current[i];
          const nextParts = nextKey.split('-');
          let nextColumn, nextFund;

          if (isMultiFunds) {
            nextFund = nextParts[nextParts.length - 1];
            nextColumn = nextParts[nextParts.length - 2];
            if (nextColumn === currentColumn && nextFund === currentFund) {
              nextIndex = i;
              found = true;
              break;
            }
          } else {
            nextColumn = nextParts[nextParts.length - 1];
            if (nextColumn === currentColumn) {
              nextIndex = i;
              found = true;
              break;
            }
          }
        }
        if (!found) {
          nextIndex = (currentIndex + 1) % totalCells;
        }
      } else if (direction === 'ArrowUp') {
        let found = false;
        for (let i = currentIndex - 1; i >= 0; i--) {
          const prevKey = cellOrder.current[i];
          const prevParts = prevKey.split('-');
          let prevColumn, prevFund;

          if (isMultiFunds) {
            prevFund = prevParts[prevParts.length - 1];
            prevColumn = prevParts[prevParts.length - 2];
            if (prevColumn === currentColumn && prevFund === currentFund) {
              nextIndex = i;
              found = true;
              break;
            }
          } else {
            prevColumn = prevParts[prevParts.length - 1];
            if (prevColumn === currentColumn) {
              nextIndex = i;
              found = true;
              break;
            }
          }
        }
        if (!found) {
          nextIndex = currentIndex - 1;
          if (nextIndex < 0) nextIndex = totalCells - 1;
        }
      }

      const nextCellKey = cellOrder.current[nextIndex];
      const nextCellRef = cellRefs.current.get(nextCellKey);
      const currentCellRef = cellRefs.current.get(currentCellKey);

      if (nextCellRef && nextCellRef.current) {
        if (currentCellRef && currentCellRef.current && currentCellKey !== nextCellKey) {
          currentCellRef.current.blur();
        }

        requestAnimationFrame(() => {
          const cellElement = nextCellRef.current;
          if (!cellElement) return;

          cellElement.focus();
          const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true });
          cellElement.dispatchEvent(clickEvent);
        });
      }
    },
    [isMultiFunds, fundKeys]
  );

  const onCellKeyDown = useCallback(
    (e, cellKey) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        navigateToCell(e.key, cellKey);
      }
    },
    [navigateToCell]
  );

  return {
    cellRefs,
    registerCell,
    onCellKeyDown
  };
};
